import { t, type TranslationKey } from "./i18n.ts";

/** Small element helper — keeps panel code readable without pulling in a framework. */
export function el<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    attrs: Record<string, string> = {},
    children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
        if (key === "class") {
            node.className = value;
        } else {
            node.setAttribute(key, value);
        }
    }
    node.append(...children);
    return node;
}

/** A text node bound to a translation key, re-rendered by applyTranslations(). */
export function tel<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    key: TranslationKey,
    attrs: Record<string, string> = {}
): HTMLElementTagNameMap[K] {
    return el(tag, { ...attrs, "data-i18n": key }, [t(key)]);
}

export type StatusTone = "ok" | "warn" | "err" | "muted";

/**
 * A status line. Its text is set imperatively rather than via data-i18n because
 * most messages carry interpolated values; the caller re-renders on language
 * change if it needs to.
 */
export class StatusLine {
    readonly node: HTMLParagraphElement;

    constructor() {
        this.node = el("p", { class: "status", role: "status" });
    }

    set(message: string, tone: StatusTone = "muted"): void {
        this.node.className = `status ${tone}`;
        this.node.textContent = message;
    }

    clear(): void {
        this.node.className = "status";
        this.node.textContent = "";
    }
}
